import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { PageMeta } from "@/components/PageMeta";
import { Users, Compass, Handshake, Loader2 } from "lucide-react";

export default function InviteLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [inviter, setInviter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Persist referral attribution (used at signup). Do NOT reward mere clicks.
    try {
      localStorage.setItem("nf_invite", code);
      localStorage.setItem("nf_src", "creator_invite");
    } catch {}
    api.get(`/invite/${code}`).then((r) => setInviter(r.data)).catch(() => setInviter(null)).finally(() => setLoading(false));
  }, [code]);

  const join = () => navigate(`/auth?mode=register&role=creator&src=creator_invite`);

  const name = inviter?.inviter_name || "A Nearfluence creator";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <PageMeta title="You've been invited to Nearfluence" description={`${name} invited you to join Nearfluence.`} />
      <header className="border-b border-zinc-200/80 bg-white/80 px-4 py-4 backdrop-blur-xl sm:px-6">
        <Logo onClick={() => navigate("/")} />
      </header>

      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="h-16 w-16 overflow-hidden rounded-2xl bg-emerald-100" data-testid="inviter-avatar">
                {inviter?.inviter_photo ? (
                  <img src={fileUrl(inviter.inviter_photo)} alt={name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-display text-2xl font-bold text-emerald-700">{name[0]}</div>
                )}
              </div>
              <Handshake className="h-6 w-6 text-zinc-300" />
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-900 text-white">
                <Users className="h-7 w-7" />
              </div>
            </div>

            <h1 data-testid="invite-heading" className="mt-6 font-display text-3xl font-bold tracking-tight text-zinc-900">
              {name} invited you to Nearfluence 👋
            </h1>
            <p className="mt-3 max-w-md text-zinc-600">
              Nearfluence helps creators discover collaborations, connect with other creators and work with local businesses across the UK.
            </p>

            <div className="mt-8 flex w-full flex-col gap-3">
              <Button data-testid="invite-join" onClick={join} className="h-12 rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                <Users className="mr-2 h-5 w-5" /> Join Nearfluence — it's free
              </Button>
              <Button data-testid="invite-browse" variant="outline" onClick={() => navigate("/discover")} className="h-12 rounded-full text-base">
                <Compass className="mr-2 h-5 w-5" /> Explore creators first
              </Button>
              <button data-testid="invite-login" onClick={() => navigate("/auth?mode=login")} className="mt-1 text-sm font-medium text-emerald-600 hover:underline">
                Already have an account? Log in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
